<template>
    <v-dialog
        v-model="isOpen"
        :width="isMobile ? '100vw' : '80vw'"
        :height="isMobile ? '100vh' : '80vh'"
        :max-width="isMobile ? '100vw' : '1200'"
        :fullscreen="isMobile"
        persistent
    >
        <v-card class="settings-modal">
            <v-card-title :class="isMobile ? 'warning white--text' : 'primary white--text'" class="d-flex align-center">
                <v-icon left color="white">{{ isMobile ? 'mdi-printer' : 'mdi-cog' }}</v-icon>
                {{ isMobile ? 'Drucker auswählen' : 'Einstellungen' }}
                <v-spacer></v-spacer>
                <v-btn
                    v-if="!isMobile"
                    text
                    color="white"
                    @click="goToOldVersion"
                    class="mr-2"
                >
                    <v-icon left>mdi-history</v-icon>
                    Alte Version
                </v-btn>
                <v-btn icon dark @click="closeModal" :disabled="isMobile && !currentPrinter">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-tabs v-if="!isMobile" v-model="activeTab" color="primary" class="settings-tabs">
                <v-tab key="printer">
                    <v-icon left>mdi-printer-settings</v-icon>
                    Drucker
                </v-tab>
                <v-tab key="layout">
                    <v-icon left>mdi-view-grid</v-icon>
                    Layout
                </v-tab>
                <v-tab key="work">
                    <v-icon left>mdi-briefcase</v-icon>
                    Arbeitsmodus
                </v-tab>
                <v-tab key="admin">
                    <v-icon left>mdi-shield-account</v-icon>
                    Admin
                </v-tab>
            </v-tabs>

            <!-- Mobile: Direct Printer Content -->
            <div v-if="isMobile">
                <v-card-text class="pa-0 printer-content mobile-printer-content">
                    <div v-if="loading" class="text-center py-8">
                        <v-progress-circular indeterminate color="warning" size="48"></v-progress-circular>
                        <p class="mt-4 text-body-1 grey--text">Drucker werden geladen...</p>
                    </div>

                    <div v-else-if="printers.length === 0" class="text-center py-8">
                        <v-icon size="64" color="grey">mdi-printer-off</v-icon>
                        <p class="mt-4 text-h6 grey--text">Keine Drucker gefunden</p>
                        <v-btn color="warning" @click="getPrinters" class="mt-2">
                            <v-icon left>mdi-refresh</v-icon>
                            Erneut versuchen
                        </v-btn>
                    </div>

                    <v-list v-else dense>
                        <v-list-item-group v-model="selectedPrinterIndex" color="warning">
                            <v-list-item
                                v-for="(printer) in printers"
                                :key="printer._id"
                                @click="selectPrinter(printer)"
                                class="mobile-printer-item"
                            >
                                <v-list-item-icon>
                                    <v-icon :color="selectedPrinter && selectedPrinter._id === printer._id ? 'warning' : 'grey'">
                                        {{ selectedPrinter && selectedPrinter._id === printer._id ? 'mdi-printer-check' : 'mdi-printer' }}
                                    </v-icon>
                                </v-list-item-icon>

                                <v-list-item-content>
                                    <v-list-item-title class="text-h6">
                                        Drucker
                                    </v-list-item-title>
                                    <v-list-item-subtitle class="text-caption">
                                        {{ formatPrinterName(printer.name || printer.url) }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>

                                <v-list-item-action>
                                    <v-icon v-if="selectedPrinter && selectedPrinter._id === printer._id" color="success">
                                        mdi-check-circle
                                    </v-icon>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list>
                </v-card-text>
            </div>

            <!-- Desktop: Tabs Items -->
            <v-tabs-items v-else v-model="activeTab" class="settings-content">
                <!-- Printer Settings Tab -->
                <v-tab-item key="printer">
                    <v-card-text class="pa-6 printer-content">
                        <div class="mb-4">
                            <p class="text-body-1 grey--text text--darken-1">
                                Wählen Sie den Drucker für den Etikettendruck aus:
                            </p>
                            <p class="text-caption grey--text" v-if="printers.length > 0">
                                {{ printers.length }} Drucker gefunden
                            </p>
                        </div>

                        <div v-if="loading" class="text-center py-8">
                            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
                            <p class="mt-4 text-body-1 grey--text">Drucker werden geladen...</p>
                        </div>

                        <div v-else-if="printers.length === 0" class="text-center py-8">
                            <v-icon size="64" color="grey">mdi-printer-off</v-icon>
                            <p class="mt-4 text-h6 grey--text">Keine Drucker gefunden</p>
                            <v-btn color="primary" @click="getPrinters" class="mt-2">
                                <v-icon left>mdi-refresh</v-icon>
                                Erneut versuchen
                            </v-btn>
                        </div>

                        <div v-else class="printer-grid">
                            <PrinterItem
                                v-for="printer in printers"
                                :key="printer._id"
                                :status="printer.online"
                                :name="printer.name"
                                :selectable="true"
                                :selected="selectedPrinter && selectedPrinter._id === printer._id"
                                @click="selectPrinter(printer)"
                            />
                        </div>
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-actions class="pa-4">
                        <v-btn @click="getPrinters" text color="grey darken-1" :loading="loading">
                            <v-icon left>mdi-refresh</v-icon>
                            Aktualisieren
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn @click="closeModal" text color="grey darken-1">
                            Abbrechen
                        </v-btn>
                        <v-btn
                            @click="confirmSelection"
                            color="primary"
                            :disabled="!selectedPrinter"
                        >
                            <v-icon left>mdi-check</v-icon>
                            Bestätigen
                        </v-btn>
                    </v-card-actions>
                </v-tab-item>

                <!-- Layout Settings Tab -->
                <v-tab-item key="layout">
                    <v-card-text class="pa-6">
                        <div class="mb-4">
                            <p class="text-body-1 grey--text text--darken-1">
                                Passen Sie die Anzeige der Artikel an:
                            </p>
                        </div>

                        <v-row class="mt-6">
                            <v-col cols="12">
                                <h3 class="text-h6 mb-4">Artikel Layout</h3>
                                
                                <v-radio-group v-model="selectedLayout" class="layout-options">
                                    <v-radio 
                                        value="linear" 
                                        class="layout-radio mb-4"
                                    >
                                        <template v-slot:label>
                                            <div class="layout-option">
                                                <div class="layout-icon">
                                                    <v-icon size="32" color="primary">mdi-view-sequential</v-icon>
                                                </div>
                                                <div class="layout-details">
                                                    <h4 class="text-subtitle-1 font-weight-medium">Linear</h4>
                                                    <p class="text-caption grey--text mb-0">
                                                        Artikel werden untereinander in einer Spalte angezeigt
                                                    </p>
                                                </div>
                                            </div>
                                        </template>
                                    </v-radio>
                                    
                                    <v-radio 
                                        value="grid" 
                                        class="layout-radio"
                                    >
                                        <template v-slot:label>
                                            <div class="layout-option">
                                                <div class="layout-icon">
                                                    <v-icon size="32" color="primary">mdi-view-grid</v-icon>
                                                </div>
                                                <div class="layout-details">
                                                    <h4 class="text-subtitle-1 font-weight-medium">Grid</h4>
                                                    <p class="text-caption grey--text mb-0">
                                                        Artikel werden nebeneinander in mehreren Spalten angezeigt
                                                    </p>
                                                </div>
                                            </div>
                                        </template>
                                    </v-radio>
                                </v-radio-group>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    
                    <v-divider></v-divider>
                    
                    <v-card-actions class="pa-4">
                        <v-spacer></v-spacer>
                        <v-btn @click="closeModal" text color="grey darken-1">
                            Abbrechen
                        </v-btn>
                        <v-btn 
                            @click="saveLayoutSettings" 
                            color="primary"
                        >
                            <v-icon left>mdi-check</v-icon>
                            Speichern
                        </v-btn>
                    </v-card-actions>
                </v-tab-item>

                <!-- Work Mode Settings Tab -->
                <v-tab-item key="work">
                    <v-card-text class="pa-6">
                        <div class="mb-4">
                            <p class="text-body-1 grey--text text--darken-1">
                                Passen Sie den Arbeitsmodus für Multicart-Bestellungen an:
                            </p>
                        </div>

                        <v-row class="mt-6">
                            <v-col cols="12">
                                <h3 class="text-h6 mb-4">Multicart Scannen</h3>

                                <div class="work-mode-section">
                                    <v-switch
                                        v-model="scannerEnabled"
                                        color="primary"
                                        class="mt-2"
                                        hide-details
                                    >
                                        <template v-slot:label>
                                            <div class="work-mode-option">
                                                <div class="work-mode-icon">
                                                    <v-icon :color="scannerEnabled ? 'primary' : 'grey'">
                                                        {{ scannerEnabled ? 'mdi-barcode-scan' : 'mdi-barcode-off' }}
                                                    </v-icon>
                                                </div>
                                                <div class="work-mode-details">
                                                    <h4 class="text-subtitle-1 font-weight-medium">
                                                        Scanner aktivieren
                                                    </h4>
                                                    <p class="text-caption grey--text mb-0">
                                                        Ermöglicht das Scannen und Verpacken von Multicart-Bestellungen direkt aus der Packliste.
                                                        Die Multicart-Buchung wird deaktiviert.
                                                    </p>
                                                </div>
                                            </div>
                                        </template>
                                    </v-switch>

                                    <v-alert
                                        v-if="scannerEnabled"
                                        type="info"
                                        text
                                        dense
                                        class="mt-4"
                                    >
                                        <div class="d-flex align-center">
                                            <v-icon left small>mdi-information</v-icon>
                                            <span class="text-caption">
                                                Im Scanner-Modus können Multicart-Artikel direkt gescannt und verpackt werden.
                                                Die Multicart-Buchungsschaltfläche wird ausgeblendet.
                                            </span>
                                        </div>
                                    </v-alert>

                                    <v-alert
                                        v-else
                                        type="warning"
                                        text
                                        dense
                                        class="mt-4"
                                    >
                                        <div class="d-flex align-center">
                                            <v-icon left small>mdi-alert</v-icon>
                                            <span class="text-caption">
                                                Scanner deaktiviert. Multicart-Bestellungen müssen über die Multicart-Buchung verarbeitet werden.
                                            </span>
                                        </div>
                                    </v-alert>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-actions class="pa-4">
                        <v-spacer></v-spacer>
                        <v-btn @click="closeModal" text color="grey darken-1">
                            Abbrechen
                        </v-btn>
                        <v-btn
                            @click="saveWorkModeSettings"
                            color="primary"
                        >
                            <v-icon left>mdi-check</v-icon>
                            Speichern
                        </v-btn>
                    </v-card-actions>
                </v-tab-item>

                <!-- Admin Settings Tab -->
                <v-tab-item key="admin" v-if="user && user.role === 'admin'">
                    <v-card-text class="pa-6">
                        <div class="admin-header mb-6">
                            <div class="d-flex align-center mb-3">
                                <v-avatar color="primary" size="48" class="mr-4">
                                    <v-icon color="white" size="28">mdi-shield-account</v-icon>
                                </v-avatar>
                                <div>
                                    <h2 class="text-h5 font-weight-bold mb-1">Admin-Bereich</h2>
                                    <p class="text-subtitle-1 grey--text mb-0">
                                        Erweiterte Funktionen und Systemverwaltung
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Packlist Reset Section -->
                        <div class="admin-section">
                            <v-card outlined class="admin-card">
                                <v-card-text class="pa-6">
                                    <div class="d-flex align-center justify-space-between">
                                        <div class="flex-grow-1 mr-4">
                                            <div class="d-flex align-center mb-3">
                                                <v-icon color="error" size="32" class="mr-3">mdi-refresh</v-icon>
                                                <h3 class="text-h6 font-weight-bold">
                                                    Packliste zurücksetzen
                                                </h3>
                                            </div>
                                            <p class="text-body-2 grey--text text--darken-1 mb-2">
                                                Setzt alle Artikel der aktuellen Packliste zurück. 
                                            </p>
                                            <p class="text-caption error--text">
                                                ⚠️ Alle Packfortschritte gehen unwiderruflich verloren!
                                            </p>
                                        </div>
                                        <v-btn 
                                            color="error" 
                                            large
                                            @click="resetPacklist"
                                            :disabled="resetInProgress"
                                            :loading="resetInProgress"
                                        >
                                            <v-icon left>mdi-refresh</v-icon>
                                            Zurücksetzen
                                        </v-btn>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>
                    </v-card-text>
                </v-tab-item>
            </v-tabs-items>
        </v-card>
    </v-dialog>
</template>

<script>
import PacklistService from '@/services/PacklistService';
import PrinterItem from '../items/PrinterItem.vue';

export default {
    name: 'SettingsModal',
    components: {
        PrinterItem
    },
    data() {
        return {
            printers: [],
            selectedPrinter: null,
            selectedPrinterIndex: null,
            selectedLayout: 'linear',
            scannerEnabled: false,
            isOpen: false,
            loading: false,
            activeTab: 0,
            resetInProgress: false,
            windowWidth: window.innerWidth
        }
    },
    computed: {
        isMobile() {
            return this.windowWidth <= 480;
        },
        packlistName() {
            return this.$route.params.id;
        }
    },
    props: {
        currentPrinter: {
            type: Object,
            default: null
        },
        openPrinterTab: {
            type: Boolean,
            default: false
        },
        user: {
            type: Object,
            default: null
        },
        show: {
            type: Boolean,
            default: true
        }
    },
    methods: {
        formatPrinterName(name) {
            // Remove "Etikettendrucker_" prefix and return just the number
            if (name && name.includes('Etikettendrucker_')) {
                return name.replace('Etikettendrucker_', '');
            }
            return name;
        },
        async getPrinters() {
            this.loading = true;
            try {
                const response = await PacklistService.getPrinters();
                this.printers = response.data || [];
            } catch (error) {
                console.error('Error getting printers:', error);
                this.printers = [];
            } finally {
                this.loading = false;
            }
        },
        selectPrinter(printer) {
            console.log('🎯 selectPrinter method called with:', printer);
            this.selectedPrinter = printer;
            console.log('🖨️ Printer selected in SettingsModal:', printer);
            console.log('🖨️ Selected printer ID:', printer._id);
            console.log('🖨️ selectedPrinter is now:', this.selectedPrinter);

            // On mobile, auto-confirm and close after selection with visual feedback
            if (this.isMobile) {
                setTimeout(() => {
                    this.confirmSelection();
                }, 300);
            }
            // On desktop, don't auto-emit - let user confirm selection
        },
        handleResize() {
            this.windowWidth = window.innerWidth;
        },
        confirmSelection() {
            if (this.selectedPrinter) {
                console.log('🖨️ Confirming printer selection:', this.selectedPrinter);
                this.$emit('select-printer', this.selectedPrinter);
            }
            this.closeModal();
        },
        saveLayoutSettings() {
            // Save layout preference to localStorage
            localStorage.setItem('articleLayout', this.selectedLayout);
            this.$emit('layout-changed', this.selectedLayout);
            this.closeModal();
        },
        saveWorkModeSettings() {
            // Save scanner mode preference to localStorage
            localStorage.setItem('multicartScannerEnabled', this.scannerEnabled.toString());
            this.$emit('scanner-mode-changed', this.scannerEnabled);
            console.log('💾 Scanner mode saved:', this.scannerEnabled);
            this.closeModal();
        },
        closeModal() {
            this.isOpen = false;
            this.selectedPrinter = this.currentPrinter; // Reset selection on close
            this.$emit('close-modal');
        },
        goToOldVersion() {
            // Navigate to old packlist version
            const currentPacklistId = this.$route.params.id;
            this.$router.push(`/packlistV2/${currentPacklistId}`);
        },
        async resetPacklist() {
            try {
                this.resetInProgress = true;
                console.log('🔄 Starting packlist reset for:', this.packlistName);

                const response = await PacklistService.resetPacklist(this.packlistName);

                if (response.status === 200) {
                    console.log('✅ Packlist reset successful');
                    
                    // Close modal and emit events for refresh
                    this.$router.push("/");
                    
                } else {
                    console.error('❌ Packlist reset failed:', response);
                }
            } catch (error) {
                console.error('❌ Error during packlist reset:', error);
            } finally {
                this.resetInProgress = false;
            }
        }
    },
    watch: {
        isOpen(newVal) {
            if (!newVal) {
                this.$emit('close-modal');
            }
        },
        show: {
            handler(newVal) {
                this.isOpen = newVal;
            },
            immediate: true
        }
    },
    async mounted() {
        // Add resize listener for mobile detection
        window.addEventListener('resize', this.handleResize);

        await this.getPrinters();

        if (this.currentPrinter) {
            this.selectedPrinter = this.currentPrinter;
            console.log('🖨️ Pre-selected current printer in SettingsModal:', this.currentPrinter);

            // Set selected index for mobile list
            const index = this.printers.findIndex(p => p._id === this.currentPrinter._id);
            if (index !== -1) {
                this.selectedPrinterIndex = index;
            }
        }

        // Load saved layout preference
        const savedLayout = localStorage.getItem('articleLayout');
        if (savedLayout) {
            this.selectedLayout = savedLayout;
        }

        // Load saved scanner mode preference
        const savedScannerMode = localStorage.getItem('multicartScannerEnabled');
        if (savedScannerMode !== null) {
            this.scannerEnabled = savedScannerMode === 'true';
            console.log('📐 Scanner mode loaded:', this.scannerEnabled);
        }

        // Auto-open printer tab if no printer is selected or openPrinterTab prop is true
        if (this.openPrinterTab || !this.currentPrinter) {
            this.activeTab = 0; // Printer tab
        }
    },
    beforeDestroy() {
        // Clean up resize listener
        window.removeEventListener('resize', this.handleResize);
    }
}
</script>

<style scoped>
.settings-modal {
    border-radius: 8px;
    overflow: hidden;
}

.settings-tabs {
    border-bottom: 1px solid #e0e0e0;
}

.settings-content {
    max-height: 70vh;
    overflow-y: auto;
}

.printer-content {
    max-height: 60vh;
    overflow-y: auto;
}

/* Mobile printer content - remove padding and white space */
.mobile-printer-content {
    height: calc(100vh - 64px); /* Full height minus header */
    max-height: calc(100vh - 64px);
    overflow-y: auto;
    padding: 0 !important;
}

.printer-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 12px;
    margin-top: 16px;
    max-height: none;
    overflow: visible;
}

/* Removed old printer-card styles since we use PrinterItem component now */

.layout-options {
    margin-top: 16px;
}

.layout-radio {
    margin-bottom: 16px !important;
}

.layout-option {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    transition: all 0.2s ease;
    width: 100%;
    min-width: 300px;
}

.layout-option:hover {
    border-color: #1976d2;
    background-color: #f5f5f5;
}

.layout-icon {
    flex-shrink: 0;
}

.layout-details {
    flex: 1;
}

/* Work Mode Section Styles */
.work-mode-section {
    margin-top: 16px;
}

.work-mode-option {
    display: flex;
    align-items: center;
    gap: 16px;
    padding-left: 8px;
}

.work-mode-icon {
    flex-shrink: 0;
}

.work-mode-details {
    flex: 1;
}

/* PrinterItem handles its own hover and selection styling */



.v-chip {
    font-size: 11px;
    height: 24px;
}

.v-radio-group {
    flex-shrink: 0;
}

.v-btn {
    text-transform: none;
}

/* Admin tab styling */
.admin-header {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 16px;
}

.admin-section {
    margin-bottom: 24px;
}

.admin-card {
    border-radius: 12px;
    transition: all 0.3s ease;
}

.admin-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .printer-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }
    
    .printer-card .v-card-text {
        padding: 16px !important;
    }
}

@media (max-width: 480px) {
    .printer-card .d-flex {
        flex-direction: column;
        align-items: flex-start;
    }

    .printer-card .v-radio-group {
        align-self: flex-end;
        margin-top: 8px;
    }

    /* Mobile printer list styling */
    .mobile-printer-item {
        min-height: 72px;
        padding: 12px 8px;
        border-bottom: 1px solid #e0e0e0;
    }

    .mobile-printer-item:last-child {
        border-bottom: none;
    }

    .mobile-printer-item .v-list-item__title {
        font-size: 1.125rem;
        font-weight: 600;
    }

    .mobile-printer-item .v-list-item__subtitle {
        font-size: 0.875rem;
        margin-top: 4px;
    }
}

/* Extra mobile styling for very small devices */
@media (max-width: 380px) {
    .mobile-printer-item {
        min-height: 80px;
        padding: 16px 12px;
    }

    .mobile-printer-item .v-list-item__title {
        font-size: 1.25rem;
    }

    .mobile-printer-item .v-list-item__subtitle {
        font-size: 1rem;
    }
}
</style>